import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    GetLoginApprovalRequest,
    ApproveLoginRequest,
    DenyLoginRequest,
    GetApprovalAssertionOptions,
    GetCredentials,
    CoerceToArrayBuffer,
    CoerceToBase64Url,
} from "./AccountStore";

const STATUS_LABELS: Record<string, string> = {
    Pending: "Pending",
    Approved: "Approved",
    Denied: "Denied",
    Expired: "Expired",
};

const ApproveLogin = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [request, setRequest] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [actionLoading, setActionLoading] = React.useState(false);
    const [hasCredentials, setHasCredentials] = React.useState(false);
    const [fido2Verified, setFido2Verified] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [done, setDone] = React.useState<"approved" | "denied" | null>(null);

    React.useEffect(() => {
        if (!id) return;

        Promise.all([
            GetLoginApprovalRequest(id).catch(() => null),
            GetCredentials().catch(() => []),
        ]).then(([req, creds]) => {
            setRequest(req);
            setHasCredentials(Array.isArray(creds) && creds.length > 0);
            setLoading(false);
        });
    }, [id]);

    const verifyWithFido2 = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const options = await GetApprovalAssertionOptions(id);
            options.challenge = CoerceToArrayBuffer(options.challenge);
            options.allowCredentials = (options.allowCredentials || []).map((c: any) => ({
                ...c,
                id: CoerceToArrayBuffer(c.id),
            }));

            const assertion = (await navigator.credentials.get({ publicKey: options })) as PublicKeyCredential;
            const assertionResponse = assertion.response as AuthenticatorAssertionResponse;

            const assertionJson = JSON.stringify({
                id: assertion.id,
                rawId: CoerceToBase64Url(assertion.rawId),
                type: assertion.type,
                extensions: (assertion as any).getClientExtensionResults(),
                response: {
                    authenticatorData: CoerceToBase64Url(assertionResponse.authenticatorData),
                    clientDataJSON: CoerceToBase64Url(assertionResponse.clientDataJSON),
                    signature: CoerceToBase64Url(assertionResponse.signature),
                    userHandle: assertionResponse.userHandle ? CoerceToBase64Url(assertionResponse.userHandle) : null,
                },
            });

            // Now approve with assertion
            await approveRequest(assertionJson);
        } catch (e: any) {
            setError("FIDO2 verification failed: " + (e.message || "cancelled"));
        } finally {
            setActionLoading(false);
        }
    };

    const approveRequest = async (assertion?: string) => {
        setActionLoading(true);
        setError(null);
        try {
            const result = await ApproveLoginRequest(id, assertion);
            if (result.success) {
                setDone("approved");
            } else {
                setError(result.message || "Approval failed.");
            }
        } catch (e: any) {
            setError("Failed to approve: " + (e.message || "unknown error"));
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async () => {
        if (hasCredentials && !fido2Verified) {
            await verifyWithFido2();
        } else {
            setActionLoading(true);
            await approveRequest();
        }
    };

    const handleDeny = async () => {
        setActionLoading(true);
        setError(null);
        try {
            await DenyLoginRequest(id);
            setDone("denied");
        } catch (e: any) {
            setError("Failed to deny: " + (e.message || "unknown error"));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50">
                <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-blue-600 text-4xl" />
            </div>
        );
    }

    if (!request) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
                    <FontAwesomeIcon icon={["fad", "shield-exclamation"]} className="text-red-500 text-5xl mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Not Found</h2>
                    <p className="text-gray-600 mb-6">This login approval request does not exist or has expired.</p>
                    <button onClick={() => navigate("/")} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    const isExpired = request.status && request.status !== "Pending";

    if (done === "approved") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-500 text-5xl mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Login Approved</h2>
                    <p className="text-gray-600">The login request has been approved. The other browser is now being signed in.</p>
                </div>
            </div>
        );
    }

    if (done === "denied") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
                    <FontAwesomeIcon icon={["fad", "times-circle"]} className="text-red-500 text-5xl mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Login Denied</h2>
                    <p className="text-gray-600">The login request has been denied.</p>
                </div>
            </div>
        );
    }

    const expiresAt = request.expiresAt ? new Date(request.expiresAt) : null;
    const minutesLeft = expiresAt ? Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60000)) : 0;

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full">
                <div className="text-center mb-6">
                    <FontAwesomeIcon icon={["fad", "shield-check"]} className="text-blue-500 text-5xl mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Login Approval Request</h2>
                    <p className="text-sm text-gray-600 mt-1">Someone wants to sign in to your account</p>
                </div>

                {isExpired ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                        <p className="text-yellow-800 font-medium">{STATUS_LABELS[request.status] || request.status}</p>
                        <p className="text-yellow-700 text-sm mt-1">This request is no longer active.</p>
                    </div>
                ) : (
                    <>
                        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-6 space-y-3">
                            <div className="flex items-start space-x-3">
                                <FontAwesomeIcon icon={["fad", "desktop"]} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Device</p>
                                    <p className="text-sm text-gray-800 break-all">{request.deviceInfo || "Unknown device"}</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-3">
                                <FontAwesomeIcon icon={["fad", "clock"]} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Requested</p>
                                    <p className="text-sm text-gray-800">
                                        {request.requestedAt ? new Date(request.requestedAt).toLocaleTimeString() : "Unknown"}
                                    </p>
                                </div>
                            </div>
                            {minutesLeft > 0 && (
                                <div className="flex items-start space-x-3">
                                    <FontAwesomeIcon icon={["fad", "hourglass-half"]} className="text-orange-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Expires in</p>
                                        <p className="text-sm text-gray-800">{minutesLeft} minute{minutesLeft !== 1 ? "s" : ""}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {hasCredentials && !fido2Verified && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                <p className="text-sm text-blue-800">
                                    <FontAwesomeIcon icon={["fad", "fingerprint"]} className="mr-2" />
                                    Your security key will be required to approve this request.
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={handleApprove}
                                disabled={actionLoading}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {actionLoading ? (
                                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                ) : (
                                    <FontAwesomeIcon icon={["fad", "check"]} className="mr-2" />
                                )}
                                {hasCredentials && !fido2Verified ? "Verify & Approve" : "Approve"}
                            </button>
                            <button
                                onClick={handleDeny}
                                disabled={actionLoading}
                                className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 font-medium py-2.5 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-2" />
                                Deny
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ApproveLogin;
